import { useEffect, useState } from "react";
import { useFieldArray } from "react-hook-form";
import { IProfileContactsForm } from "../../../../../../Interfaces/formValue-interfaces";
import { IProfileContactsDialog } from "./profileContactsDialog";
import AuthInput from "../../../../../../Components/Inputs/AuthInput/authInput";
import TelInput from "../../../../../../Components/Inputs/TelInput/telInput";
import useLanguagesHooks from "../../../../../../Components/Languages/setLanguage-hooks";
import { RedClose, RedPlus } from "../../../../../../Assets/Icons";
import styles from './profileContactsDialog.module.scss';

const useProfileContactsDialog = (props: IProfileContactsDialog) => {
    const { control, register, data, watch, trigger } = props;
    const { t } = useLanguagesHooks();
    const phoneNumbers = watch("phone_numbers");
    const { fields, append, remove } = useFieldArray({
        control,
        name: "phone_numbers",
    });
    const [isCurrentValid, setIsCurrentValid] = useState<boolean>(false);

    useEffect(() => {
        const subscription = watch((values) => {
            const currentValue = values[data.id];

            if (Array.isArray(currentValue)) {
                const allValid =
                    currentValue.length > 0 &&
                    currentValue.every(
                        (item) => item?.value && validatePhoneNumber(item.value)
                    );

                setIsCurrentValid(allValid);
            } else if (typeof currentValue === "string") {
                switch (data.id) {
                    case "facebook":
                    case "instagram":
                    case "telegram":
                        setIsCurrentValid(!!currentValue && validateUsername(currentValue));
                        break;
                    case "whatsapp":
                    case "viber":
                        setIsCurrentValid(!!currentValue && validatePhoneNumber(currentValue));
                        break;
                    default:
                        setIsCurrentValid(false);
                }
            }
        });

        return () => subscription.unsubscribe();
    }, [watch, data.id]);

    const validateUsername = (value: string) => {
        return value.trim().length >= 3;
    };
    
    const validatePhoneNumber = (value: string) => {
        const cleaned = value.replace(/\s+/g, "");
        return !!cleaned.match(/^\+?[0-9]{7,15}$/);
    };

    useEffect(() => {
        trigger();
    }, [fields.length]);

    const onAddPhoneNumber = (index: number) => {
        append({ id: String(index), value: "" });
        trigger();
    };

    const onDeletePhoneNumber = (index: number) => {
        remove(index);
        trigger();
    };

    const renderFormInput = (type: keyof IProfileContactsForm) => {
        switch (type) {
            case "facebook":
            case "instagram":
            case "telegram":
                return (
                    <div className={styles.input}>
                        <AuthInput
                            register={register}
                            registerName={type}
                            patternValue={/^[a-zA-Z0-9._]{2,30}$/}
                            required
                        />
                    </div>
                );

            case "whatsapp":
            case "viber":
                return (
                    <div className={styles.input}>
                        <TelInput
                            name={type}
                            control={control}
                        />
                    </div>
                );

            case "phone_numbers":
                return phoneNumbers?.map((item, index) => (
                    <div key={fields[index]?.id || index} className={styles.phoneInputBox}>
                        <div className={styles.phoneInputRow}>
                            <div className={styles.input}>
                                <TelInput
                                    name={`phone_numbers.${index}.value`}
                                    control={control}
                                    showTextError={false}
                                />
                            </div>
                            {index !== 0 && (
                                <img
                                    src={RedClose}
                                    alt='delete.dvg'
                                    onClick={() => onDeletePhoneNumber(index)}
                                    className={styles.deletePhoneNumber}
                                />
                            )}
                        </div>
                        {index === phoneNumbers.length - 1 && (
                            <div className={styles.addPhoneButtonRow}>
                                <div className={styles.addButton} onClick={() => onAddPhoneNumber(phoneNumbers.length)}>
                                    <img src={RedPlus} alt='plus.svg' />
                                    <p>{t('My_Business.Business_Profile.Contact.Add_Phone')}</p>
                                </div>
                            </div>
                        )}
                    </div>
                ));
            default:
                return <></>;
        }
    };

    return {
        isCurrentValid,
        renderFormInput,
    };
};

export default useProfileContactsDialog;
